import type { PendingAgentImage, PendingAgentMediaType } from "@/components/agent/agent-image-attach"
import type { ShotFrameSource } from "@/lib/workspace-api"

const DB_NAME = "it2tv-agent-composer-images"
const STORE_NAME = "blobs"
const DB_VERSION = 1

/** Черновик старше этого срока удаляется при следующем открытии базы. */
export const COMPOSER_IMAGE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

export type ComposerImageManifestItem =
  | { kind: "upload"; key: string; name: string; type: string; lastModified: number; mediaType: PendingAgentMediaType }
  | {
      kind: "library"
      key: string
      source: ShotFrameSource
      label: string
      previewUrl: string | null
      mediaType: PendingAgentMediaType
    }

export type ComposerImageBlobRecord = {
  blob: Blob
  updatedAt: number
}

export type ComposerImageBlobStore = {
  get(key: string): Promise<ComposerImageBlobRecord | null>
  put(key: string, record: ComposerImageBlobRecord): Promise<void>
  /** Обновляет срок у уже лежащих блобов, не пересылая байты со страницы. */
  touch(keys: readonly string[], now: number): Promise<void>
  deleteMany(keys: readonly string[]): Promise<void>
  listKeys(prefix: string): Promise<string[]>
}

export type ComposerImageManifestStore = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export type ComposerImageStores = {
  manifest: ComposerImageManifestStore
  blobs: ComposerImageBlobStore
}

export type WriteAgentComposerImagesResult = {
  /** Загрузки, которых нет в манифесте: после обновления страницы их не будет. */
  omittedUploadKeys: string[]
  quotaExceeded: boolean
}

type StoreOptions = {
  stores?: ComposerImageStores
  now?: number
  createObjectURL?: (blob: Blob) => string
}

/** sessionStorage key with the ordered manifest. Bytes live in IndexedDB. */
export function agentComposerImagesManifestKey(draftKey: string): string {
  return `${draftKey}:images`
}

export function composerImageRecordExpired(updatedAt: number, now: number): boolean {
  return !Number.isFinite(updatedAt) || now - updatedAt > COMPOSER_IMAGE_MAX_AGE_MS
}

let operationQueue: Promise<void> = Promise.resolve()

function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const run = operationQueue.then(task, task)
  operationQueue = run.then(
    () => undefined,
    () => undefined,
  )
  return run
}

export function readAgentComposerImages(draftKey: string, options?: StoreOptions): Promise<PendingAgentImage[]> {
  return enqueue(() => readNow(draftKey, options).catch(() => []))
}

export function writeAgentComposerImages(
  draftKey: string,
  images: readonly PendingAgentImage[],
  options?: StoreOptions,
): Promise<WriteAgentComposerImagesResult> {
  return enqueue(() =>
    writeNow(draftKey, images, options).catch(() => ({ omittedUploadKeys: [], quotaExceeded: false })),
  )
}

/** Продлевает жизнь уже сохранённых загрузок. Новые байты не пишет. */
export function touchAgentComposerImages(
  draftKey: string,
  images: readonly PendingAgentImage[],
  options?: StoreOptions,
): Promise<void> {
  return enqueue(() => touchNow(draftKey, images, options).catch(() => undefined))
}

function blobPrefix(draftKey: string): string {
  return `${draftKey}\0`
}

function blobStorageKey(draftKey: string, imageKey: string): string {
  return `${blobPrefix(draftKey)}${imageKey}`
}

async function readNow(draftKey: string, options?: StoreOptions): Promise<PendingAgentImage[]> {
  const stores = options?.stores ?? defaultComposerImageStores()
  const createObjectURL = options?.createObjectURL ?? ((blob: Blob) => URL.createObjectURL(blob))
  const raw = stores.manifest.getItem(agentComposerImagesManifestKey(draftKey))
  if (!raw) return []

  const images: PendingAgentImage[] = []
  try {
    for (const item of parseManifest(raw)) {
      if (item.kind === "library") {
        images.push({
          key: item.key,
          kind: "library",
          source: item.source,
          label: item.label,
          previewUrl: item.previewUrl,
          mediaType: item.mediaType,
        })
        continue
      }
      const record = await stores.blobs.get(blobStorageKey(draftKey, item.key))
      if (!record) continue
      const file = new File([record.blob], item.name || "image", {
        type: item.type || record.blob.type,
        lastModified: Number.isFinite(item.lastModified) ? item.lastModified : Date.now(),
      })
      images.push({
        key: item.key,
        kind: "upload",
        file,
        previewUrl: createObjectURL(file),
        mediaType: item.mediaType,
      })
    }
    return images
  } catch {
    revokeUploadPreviews(images)
    return []
  }
}

async function writeNow(
  draftKey: string,
  images: readonly PendingAgentImage[],
  options?: StoreOptions,
): Promise<WriteAgentComposerImagesResult> {
  const stores = options?.stores ?? defaultComposerImageStores()
  const now = options?.now ?? Date.now()
  const manifestKey = agentComposerImagesManifestKey(draftKey)
  const prefix = blobPrefix(draftKey)

  if (images.length === 0) {
    stores.manifest.removeItem(manifestKey)
    await stores.blobs.deleteMany(await stores.blobs.listKeys(prefix))
    return { omittedUploadKeys: [], quotaExceeded: false }
  }

  const manifest: ComposerImageManifestItem[] = []
  const kept = new Set<string>()
  const omittedUploadKeys: string[] = []
  let quotaExceeded = false

  for (const image of images) {
    if (image.kind === "library") {
      const item = libraryManifestItem(image)
      if (item) manifest.push(item)
      continue
    }

    const storageKey = blobStorageKey(draftKey, image.key)
    try {
      await stores.blobs.put(storageKey, { blob: image.file, updatedAt: now })
      manifest.push(uploadManifestItem(image))
      kept.add(storageKey)
    } catch (error) {
      const existing = await stores.blobs.get(storageKey).catch(() => null)
      if (existing) {
        manifest.push(uploadManifestItem(image))
        kept.add(storageKey)
        continue
      }
      omittedUploadKeys.push(image.key)
      if (isQuotaError(error)) quotaExceeded = true
    }
  }

  if (manifest.length === 0) stores.manifest.removeItem(manifestKey)
  else stores.manifest.setItem(manifestKey, JSON.stringify(manifest))

  const stale = (await stores.blobs.listKeys(prefix)).filter((key) => !kept.has(key))
  await stores.blobs.deleteMany(stale)
  return { omittedUploadKeys, quotaExceeded }
}

async function touchNow(draftKey: string, images: readonly PendingAgentImage[], options?: StoreOptions): Promise<void> {
  const stores = options?.stores ?? defaultComposerImageStores()
  const now = options?.now ?? Date.now()
  const keys = images.flatMap((image) => (image.kind === "upload" ? [blobStorageKey(draftKey, image.key)] : []))
  if (keys.length === 0) return
  await stores.blobs.touch(keys, now)
}

function uploadMediaType(stored: unknown, type: string, name: string): PendingAgentMediaType {
  if (stored === "video" || stored === "image") return stored
  const normalized = type === "image/jpg" ? "image/jpeg" : type
  if (normalized.startsWith("video/") || /\.(mp4|webm|mov)$/i.test(name)) return "video"
  return "image"
}

function uploadManifestItem(image: Extract<PendingAgentImage, { kind: "upload" }>): ComposerImageManifestItem {
  return {
    kind: "upload",
    key: image.key,
    name: image.file.name || "image",
    type: image.file.type,
    lastModified: image.file.lastModified,
    mediaType: image.mediaType === "video" ? "video" : "image",
  }
}

function libraryManifestItem(image: Extract<PendingAgentImage, { kind: "library" }>): ComposerImageManifestItem | null {
  const source = parseSource(image.source)
  if (!source || image.key === "") return null
  return {
    kind: "library",
    key: image.key,
    source,
    label: image.label,
    previewUrl: typeof image.previewUrl === "string" ? image.previewUrl : null,
    mediaType: image.mediaType === "video" ? "video" : "image",
  }
}

function parseManifest(raw: string): ComposerImageManifestItem[] {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []

  const items: ComposerImageManifestItem[] = []
  const seen = new Set<string>()
  for (const entry of parsed) {
    const item = parseManifestItem(entry)
    if (!item || seen.has(item.key)) continue
    seen.add(item.key)
    items.push(item)
  }
  return items
}

function parseManifestItem(entry: unknown): ComposerImageManifestItem | null {
  if (typeof entry !== "object" || entry === null) return null
  const record = entry as { kind?: unknown; key?: unknown }
  if (typeof record.key !== "string" || record.key === "") return null

  if (record.kind === "upload") {
    const upload = entry as { name?: unknown; type?: unknown; lastModified?: unknown; mediaType?: unknown }
    const name = typeof upload.name === "string" && upload.name !== "" ? upload.name : "image"
    const type = typeof upload.type === "string" ? upload.type : ""
    return {
      kind: "upload",
      key: record.key,
      name,
      type,
      lastModified: typeof upload.lastModified === "number" ? upload.lastModified : Date.now(),
      mediaType: uploadMediaType(upload.mediaType, type, name),
    }
  }

  if (record.kind === "library") {
    const library = entry as { source?: unknown; label?: unknown; previewUrl?: unknown; mediaType?: unknown }
    const source = parseSource(library.source)
    if (!source) return null
    return {
      kind: "library",
      key: record.key,
      source,
      label: typeof library.label === "string" ? library.label : "",
      previewUrl: typeof library.previewUrl === "string" ? library.previewUrl : null,
      mediaType: library.mediaType === "video" ? "video" : "image",
    }
  }

  return null
}

function parseSource(value: unknown): ShotFrameSource | null {
  if (typeof value !== "object" || value === null) return null
  const source = value as { entity_type?: unknown; entity_id?: unknown; state_id?: unknown }
  if (typeof source.entity_type !== "string" || source.entity_type === "") return null
  if (typeof source.entity_id !== "number" || !Number.isInteger(source.entity_id) || source.entity_id <= 0) return null
  const entityType = source.entity_type as ShotFrameSource["entity_type"]
  if (source.state_id == null) return { entity_type: entityType, entity_id: source.entity_id }
  if (source.state_id !== "first" && source.state_id !== "last") return null
  return { entity_type: entityType, entity_id: source.entity_id, state_id: source.state_id }
}

function isQuotaError(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("name" in error)) return false
  const name = (error as { name: unknown }).name
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED"
}

function revokeUploadPreviews(images: readonly PendingAgentImage[]) {
  for (const image of images) {
    if (image.kind === "upload" && image.previewUrl.startsWith("blob:")) URL.revokeObjectURL(image.previewUrl)
  }
}

function defaultComposerImageStores(): ComposerImageStores {
  return {
    manifest: sessionManifestStore(),
    blobs: indexedDbBlobStore(),
  }
}

function sessionManifestStore(): ComposerImageManifestStore {
  return {
    getItem(key) {
      try {
        return sessionStorage.getItem(key)
      } catch {
        return null
      }
    },
    setItem(key, value) {
      try {
        sessionStorage.setItem(key, value)
      } catch {
        // Квота sessionStorage или приватный режим: байты могли сохраниться, список — нет.
      }
    },
    removeItem(key) {
      try {
        sessionStorage.removeItem(key)
      } catch {
        // ignore
      }
    },
  }
}

let databasePromise: Promise<IDBDatabase> | null = null

function indexedDbBlobStore(): ComposerImageBlobStore {
  return {
    get(key) {
      return openDatabase().then(
        (db) =>
          new Promise((resolve, reject) => {
            let settled = false
            const fail = (error: unknown) => {
              if (settled) return
              settled = true
              reject(error)
            }
            const succeed = (value: ComposerImageBlobRecord | null) => {
              if (settled) return
              settled = true
              resolve(value)
            }
            const tx = db.transaction(STORE_NAME, "readonly")
            const request = tx.objectStore(STORE_NAME).get(key)
            request.onsuccess = () => succeed(normalizeRecord(request.result))
            tx.onabort = () => fail(tx.error ?? request.error ?? new Error("composer image read aborted"))
            tx.onerror = () => fail(tx.error ?? request.error ?? new Error("composer image read failed"))
          }),
      )
    },
    put(key, record) {
      return idbTransaction("readwrite", (store) => {
        store.put(record, key)
      })
    },
    touch(keys, now) {
      if (keys.length === 0) return Promise.resolve()
      return openDatabase().then(
        (db) =>
          new Promise((resolve, reject) => {
            const finish = settleTransaction(resolve, reject)
            const tx = db.transaction(STORE_NAME, "readwrite")
            const store = tx.objectStore(STORE_NAME)
            for (const key of keys) {
              const request = store.get(key)
              request.onsuccess = () => {
                const record = normalizeRecord(request.result)
                if (!record) return
                store.put({ blob: record.blob, updatedAt: now }, key)
              }
            }
            tx.oncomplete = () => finish()
            tx.onabort = () => finish(tx.error ?? new Error("composer image touch aborted"))
            tx.onerror = () => finish(tx.error ?? new Error("composer image touch failed"))
          }),
      )
    },
    deleteMany(keys) {
      if (keys.length === 0) return Promise.resolve()
      return idbTransaction("readwrite", (store) => {
        for (const key of keys) store.delete(key)
      })
    },
    listKeys(prefix) {
      return openDatabase().then(
        (db) =>
          new Promise((resolve, reject) => {
            const keys: string[] = []
            const finish = settleTransaction(() => resolve(keys), reject)
            const tx = db.transaction(STORE_NAME, "readonly")
            const request = tx.objectStore(STORE_NAME).openCursor(IDBKeyRange.bound(prefix, `${prefix}\uffff`))
            request.onsuccess = () => {
              const cursor = request.result
              if (!cursor) return
              if (typeof cursor.key === "string" && cursor.key.startsWith(prefix)) keys.push(cursor.key)
              cursor.continue()
            }
            tx.oncomplete = () => finish()
            tx.onabort = () => finish(tx.error ?? new Error("composer image list aborted"))
            tx.onerror = () => finish(tx.error ?? new Error("composer image list failed"))
          }),
      )
    },
  }
}

function idbTransaction(mode: IDBTransactionMode, run: (store: IDBObjectStore) => void): Promise<void> {
  return openDatabase().then(
    (db) =>
      new Promise((resolve, reject) => {
        const finish = settleTransaction(resolve, reject)
        const tx = db.transaction(STORE_NAME, mode)
        try {
          run(tx.objectStore(STORE_NAME))
        } catch (error) {
          finish(error)
          return
        }
        tx.oncomplete = () => finish()
        tx.onabort = () => finish(tx.error ?? new Error("composer image storage aborted"))
        tx.onerror = () => finish(tx.error ?? new Error("composer image storage failed"))
      }),
  )
}

function openDatabase(): Promise<IDBDatabase> {
  if (!databasePromise) {
    databasePromise = openDatabaseOnce().catch((error: unknown) => {
      databasePromise = null
      throw error
    })
  }
  return databasePromise
}

async function openDatabaseOnce(): Promise<IDBDatabase> {
  const db = await requestDatabase()
  try {
    await sweepDatabase(db, Date.now())
  } catch {
    // База остаётся рабочей, даже если уборка старых черновиков не удалась.
  }
  return db
}

function requestDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error("Failed to open composer image storage"))
  })
}

function sweepDatabase(db: IDBDatabase, now: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const finish = settleTransaction(resolve, reject)
    const tx = db.transaction(STORE_NAME, "readwrite")
    const request = tx.objectStore(STORE_NAME).openCursor()
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) return
      const updatedAt = (cursor.value as { updatedAt?: unknown } | null)?.updatedAt
      if (composerImageRecordExpired(typeof updatedAt === "number" ? updatedAt : Number.NaN, now)) cursor.delete()
      cursor.continue()
    }
    tx.oncomplete = () => finish()
    tx.onabort = () => finish(tx.error ?? new Error("composer image sweep aborted"))
    tx.onerror = () => finish(tx.error ?? new Error("composer image sweep failed"))
  })
}

function normalizeRecord(value: unknown): ComposerImageBlobRecord | null {
  if (typeof value !== "object" || value === null || !("blob" in value)) return null
  const blob = (value as { blob?: unknown }).blob
  const updatedAt = (value as { updatedAt?: unknown }).updatedAt
  if (!(blob instanceof Blob)) return null
  return { blob, updatedAt: typeof updatedAt === "number" ? updatedAt : Number.NaN }
}

function settleTransaction(resolve: () => void, reject: (error: unknown) => void): (error?: unknown) => void {
  let settled = false
  return (error?: unknown) => {
    if (settled) return
    settled = true
    if (error === undefined) resolve()
    else reject(error)
  }
}
